import { SelectionModel } from '@angular/cdk/collections';
import {
  DestroyRef,
  Directive,
  OnInit,
  WritableSignal,
  computed,
  effect,
  inject,
  model,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { EMPTY, Observable, catchError, of, switchMap } from 'rxjs';

import { ListResponse } from '@models/helpers';
import { FiltersToApply } from '@shared/components/data-list/common/models/data-list-command.interface';
import {
  CipoTableOptions,
  TableColumn,
  TablePaginator,
  TableRow,
} from '@shared/components/data-list/common/models/table.interface';
import { PAGE_SIZE_OPTIONS } from '@shared/consts';
import { NotificationService } from '@shared/services';

@Directive()
export abstract class DataListWrapper<T = TableRow> implements OnInit {
  protected destroyRef = inject(DestroyRef);
  protected readonly notificationService = inject(NotificationService);

  items = signal<T[]>([]);
  isLoading = signal<boolean>(false);

  filtersToApply = model<FiltersToApply>({});
  sort = signal<Sort>(null);
  pageIndex = signal<number>(0);
  pageSize = signal<number>(PAGE_SIZE_OPTIONS.default[2]);

  abstract tableColumns: WritableSignal<TableColumn[]>;
  abstract tableOptions: WritableSignal<CipoTableOptions>;

  protected selection = signal<SelectionModel<TableRow>>(null);

  private fetchParams = computed(() => ({
    filters: this.filtersToApply(),
    sort: this.sort(),
    page: this.pageIndex(),
    size: this.pageSize(),
  }));

  constructor() {
    effect(() => {
      this.filtersToApply();
      untracked(() => {
        if (this.pageIndex() !== 0) {
          this.pageIndex.set(0);
        }
      });
    });

    toObservable(this.fetchParams)
      .pipe(
        switchMap(({ filters, sort, page, size }) => {
          if (!this.shouldFetchItems()) {
            return EMPTY;
          }

          this.isLoading.set(true);
          if (page === 0) {
            this.items.set(undefined as unknown as T[]);
            this.selection()?.clear();
          }

          const defaultSort = untracked(() => this.tableOptions())?.sort;
          return this.loadItems(page + 1, size, filters, sort || defaultSort).pipe(
            catchError(err => {
              this.onLoadError(err);
              return of({ data: [], records: 0 } as ListResponse<T>);
            }),
          );
        }),
        takeUntilDestroyed(),
      )
      .subscribe({
        next: res => {
          this.items.set(res.data);
          this.syncPaginationLength(res.records);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  ngOnInit(): void {
    const options = this.tableOptions();
    if (options?.pagination?.pageSize) {
      this.pageSize.set(options.pagination.pageSize);
    }
  }

  /**
   * When false, search param changes do not trigger loadItems (e.g. calendar/gantt view on dynamic module).
   */
  protected shouldFetchItems(): boolean {
    return true;
  }

  protected onLoadError(_err: unknown): void {
    this.notificationService.error('errors.unidentifiedError');
  }

  /**
   * Abstract method to fetch data. Must be implemented by subclasses.
   * @param page 1-based page index
   * @param size items per page
   * @param filters search criteria and filters emitted by DataListCommandComponent
   * @param sort the current sorting applied to the table
   */
  abstract loadItems(page: number, size: number, filters?: FiltersToApply, sort?: Sort): Observable<ListResponse<T>>;

  /**
   * Event bound to (paginationupdated) loop from the child datalist component.
   */
  onPaginationUpdated(event: PageEvent | TablePaginator) {
    this.pageIndex.set(event.pageIndex || 0);
    if (event.pageSize != null) {
      this.pageSize.set(event.pageSize);
    }
    this.tableOptions.update(options => {
      if (!options?.pagination) {
        return options;
      }
      return {
        ...options,
        pagination: {
          ...options.pagination,
          pageIndex: event.pageIndex ?? 0,
          ...(event.pageSize != null ? { pageSize: event.pageSize } : {}),
        },
      };
    });
  }

  /**
   * Event bound to (sortchanged) loop from the child datalist component.
   */
  onSortChanged(event: Sort) {
    this.pageIndex.set(0);
    this.sort.set(event);
  }

  /**
   * Triggers a manual reload of the current view (same page, sort, filters).
   */
  refreshList() {
    this.pageIndex.set(0);
    this.filtersToApply.update(f => ({ ...f }));
  }

  /**
   * Ensures the pagination controls know the total number of items loaded
   */
  private syncPaginationLength(totalRecords: number) {
    this.tableOptions.update(options => {
      if (options?.pagination) {
        return {
          ...options,
          pagination: {
            ...options.pagination,
            length: totalRecords,
            pageIndex: this.pageIndex(),
            pageSize: this.pageSize(),
          },
        };
      }
      return options;
    });
  }
}
